import { z } from 'zod';

import type { ActivityDefinition } from './types.js';

import { trimmed } from './_checks.js';
import { draftFields, Feedback, questionFields } from './_shared.js';
import { SWIPE_CARD_IMAGE } from './swipeCardImage.js';
import { activityIssue, asList, asRecord } from './types.js';

const issue = activityIssue('swipe_select');

export const SwipeCard = z.looseObject({
  question: z.string().optional(),
  correctDirection: z.enum(['left', 'right']).optional(),
  feedback: Feedback.optional(),
  [SWIPE_CARD_IMAGE.slugKey]: z.string().optional(),
  [SWIPE_CARD_IMAGE.altKey]: z.string().optional(),
  [SWIPE_CARD_IMAGE.urlKey]: z.string().optional(),
});

/**
 * A swipe deck. Each card is a statement swiped one way or the other, and the deck's
 * two sides are named once for the whole activity.
 *
 * The answer-integrity checks a deck also needs — a deck all swiping one way, a deck
 * running strictly left-right-left — already live in `rules/shared/activityChecks.ts`
 * and run document-wide. This module owns the per-card shape those rules assume, so
 * the two are complementary rather than a second copy of the same judgement.
 *
 * A card can carry an image above its statement. A content-asset image needs its own alt
 * text; a stored image URL is a legacy image that still plays, so it is only a warning.
 * Not every card needs an image, so a deck with images on some cards is fine.
 */
export const swipeSelect: ActivityDefinition = {
  type: 'swipe_select',
  summary: 'A swipe deck — each card is swiped left or right, with the two sides named.',
  setup: z.looseObject({
    ...draftFields,
    ...questionFields,
    leftLabel: z.string().optional(),
    rightLabel: z.string().optional(),
    swipeLeftLabel: z.string().optional(),
    swipeRightLabel: z.string().optional(),
    cards: z.array(SwipeCard).optional(),
  }),
  check: (setup, ctx) => {
    const cards = asList(setup.cards);
    const issues = [];

    if (!cards.length) {
      issues.push(issue('empty', 'No cards are written — the deck has nothing to swipe.', [...ctx.path, 'setup', 'cards'], ctx));
    }

    const left = trimmed(setup.leftLabel) || trimmed(setup.swipeLeftLabel);
    const right = trimmed(setup.rightLabel) || trimmed(setup.swipeRightLabel);
    if (cards.length && (!left || !right)) {
      issues.push(issue(
        'unnamedSides',
        'A swipe deck needs both sides named (leftLabel and rightLabel) or the player cannot tell what a swipe means.',
        [...ctx.path, 'setup'],
        ctx,
        { leftLabel: setup.leftLabel, rightLabel: setup.rightLabel },
      ));
    }

    for (const [index, raw] of cards.entries()) {
      const card = asRecord(raw);
      const path = [...ctx.path, 'setup', 'cards', index];
      if (!trimmed(card.question)) {
        issues.push(issue('cardText', `Card ${index + 1} has no statement on it.`, [...path, 'question'], ctx, raw));
      }
      if (card[SWIPE_CARD_IMAGE.urlKey] !== undefined) {
        issues.push(issue(
          'cardImageUrl',
          `Card ${index + 1} stores an image URL; pick its image from the content assets instead.`,
          [...path, SWIPE_CARD_IMAGE.urlKey],
          ctx,
          card[SWIPE_CARD_IMAGE.urlKey],
          'warning',
        ));
      }
      if (trimmed(card[SWIPE_CARD_IMAGE.slugKey]) && !trimmed(card[SWIPE_CARD_IMAGE.altKey])) {
        issues.push(issue('cardImageAlt', `Card ${index + 1}'s image has no alt text.`, [...path, SWIPE_CARD_IMAGE.altKey], ctx, raw));
      }
    }

    return issues;
  },
};
